import { clockSync } from './clock-sync.js';

/**
 * Tick bayatlık (staleness) eşik motoru.
 *
 * decision_pwa_resilience.md §3 eşikleri:
 *   <5sn      -> fresh    (normal)
 *   5-30sn    -> dim      (opacity %70)
 *   30-300sn  -> warn     (sarı badge "X sn önce")
 *   >300sn    -> critical (kırmızı tam ekran bandı + tüm semboller dim)
 */
export type StalenessLevel = 'fresh' | 'dim' | 'warn' | 'critical';

export interface StalenessResult {
    level: StalenessLevel;
    ageMs: number;
    ageLabel: string;
}

const THRESHOLDS = {
    dim: 5_000,
    warn: 30_000,
    critical: 300_000,
} as const;

/** clock-sync henüz yeterli sample toplamadıysa staleness güvenilmez — fresh kabul. */
const MIN_SAMPLES = 3;

export function evaluateStaleness(serverTs: number | undefined): StalenessResult {
    if (serverTs === undefined || !Number.isFinite(serverTs) || clockSync.samples < MIN_SAMPLES) {
        return { level: 'fresh', ageMs: 0, ageLabel: '' };
    }

    const ageMs = Math.max(0, clockSync.ageMs(serverTs));
    let level: StalenessLevel = 'fresh';
    if (ageMs >= THRESHOLDS.critical) {
        level = 'critical';
    } else if (ageMs >= THRESHOLDS.warn) {
        level = 'warn';
    } else if (ageMs >= THRESHOLDS.dim) {
        level = 'dim';
    }

    return { level, ageMs, ageLabel: formatAge(ageMs) };
}

/** Verilen tick'lerden en taze olanın global staleness'i. */
export function globalStaleness(serverTimestamps: Array<number | undefined>): StalenessResult {
    const valid = serverTimestamps.filter((t): t is number => typeof t === 'number' && Number.isFinite(t));
    if (valid.length === 0) {
        return { level: 'fresh', ageMs: 0, ageLabel: '' };
    }
    return evaluateStaleness(Math.max(...valid));
}

function formatAge(ageMs: number): string {
    const sec = Math.floor(ageMs / 1000);
    if (sec < 60) return `${sec} sn önce`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} dk önce`;
    const hour = Math.floor(min / 60);
    return `${hour} sa önce`;
}
